import React, { useState } from "react";
import { Grid, Paper, Box, Typography, Modal, Backdrop, Fade, IconButton } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

interface UploadFileActionProps {
    actionValue: any[];
    heading?: string;
}

const UploadFileAction: React.FC<UploadFileActionProps> = ({ actionValue, heading }) => {
    const [open, setOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<{ url: string; type: string } | null>(null);

    const handleOpen = (file: any) => {
        setSelectedFile(file);
        setOpen(true);
    };

    const handleClose = () => {
        setSelectedFile(null);
        setOpen(false);
    };

    const getFileIcon = (fileType: string) => {
        switch (fileType.toLowerCase()) {
            case "pdf":
                return <PictureAsPdfIcon color="error" />;
            case "png":
            case "jpg":
            case "jpeg":
                return <ImageIcon color="primary" />;
            case "mp3":
            case "wav":
            case "aac":
                return <AudioFileIcon color="secondary" />;
            case "mp4":
            case "mov":
            case "avi":
                return <VideoFileIcon color="primary" />;
            default:
                return <InsertDriveFileIcon color="action" />;
        }
    };

    const renderFilePreview = (file: any) => {
        if (!file.url) return null;

        const fileType = file.type.toLowerCase();
        if (["png", "jpg", "jpeg"].includes(fileType)) {
            return <img src={file.url} alt="Uploaded file" style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, cursor: "pointer" }} onClick={() => handleOpen(file)} />;
        }
        if (["mp3", "wav", "aac"].includes(fileType)) {
            return (
                <audio controls style={{ width: "100%" }}>
                    <source src={file.url} type={`audio/${fileType}`} />
                </audio>
            );
        }
        if (["mp4", "mov", "avi"].includes(fileType)) {
            return (
                <video controls style={{ width: "100%", maxHeight: 150, borderRadius: 8 }}>
                    <source src={file.url} type={`video/${fileType}`} />
                </video>
            );
        }
        if (fileType === "pdf") {
            return (
                <iframe
                    src={`https://docs.google.com/gview?url=${file.url}&embedded=true`}
                    style={{ width: "100%", height: "200px", border: "none" }}
                    title="PDF Preview"
                />
            );
        }    
        if (["doc", "docx", "xls", "xlsx"].includes(fileType)) {
            return (
                <iframe
                    src={`https://docs.google.com/gview?url=${file.url}&embedded=true`}
                    style={{ width: "100%", height: "200px", border: "none" }}
                    title="Document Preview"
                />
            );
        }
            
        return <a href={file.url} target="_blank" rel="noopener noreferrer">Download File</a>;
    };

    return (
        <>
            <Grid container spacing={2}>
                {actionValue.map((item, itemIndex) => (
                    <React.Fragment key={itemIndex}>
                        <Grid item xs={12}>
                            <Typography sx={{ fontWeight: "bold", marginBottom: "10px", marginTop: "15px" }}>
                                {item.config_details?.value?.title || "Uploaded File"} :
                            </Typography>
                        </Grid>
                        {item.files?.map((file: any, fileIndex: number) => (
                            <Grid item xs={12} sm={6} key={`${itemIndex}-${fileIndex}`}>
                                <Paper
                                    sx={{
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "column",
                                        textAlign: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, overflow: "hidden" }} onClick={() => handleOpen(file)}>
                                        <Box sx={{ mr: 1 }}>{getFileIcon(file.type)}</Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {(() => {
                                                    const fileName = file.url.split("/").pop();
                                                    return fileName.length > 20 ? fileName.slice(0, 17) + "..." : fileName;
                                                })()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Type: {file.type.toUpperCase()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ mt: 1, width: "100%", textAlign: "center" }}>
                                        {renderFilePreview(file)}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </React.Fragment>
                ))}
            </Grid>

            <Modal open={open} onClose={handleClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
                <Fade in={open}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "background.paper",
                            boxShadow: 24,
                            p: 2,
                            borderRadius: 2,
                            maxWidth: "90vw",
                            maxHeight: "90vh",
                            overflow: "auto",
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "grey.500",
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {selectedFile && selectedFile.type && ["png", "jpg", "jpeg"].includes(selectedFile.type.toLowerCase()) && (
                            <img src={selectedFile.url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }} />
                        )}
                        {selectedFile && selectedFile.type && ["mp4", "mov", "avi"].includes(selectedFile.type.toLowerCase()) && (
                            <video controls style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}>
                                <source src={selectedFile.url} type={`video/${selectedFile.type.toLowerCase()}`} />
                            </video>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};

export default UploadFileAction;
